import type { ManagerRankingReport } from '@repo/sdk/gen'
import { rankingDownloadReportFile } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { DetailSection } from '@repo/ui/components/detail-section'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { startDownload } from '@repo/ui/download'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { CheckCircle2, Download, MessageSquare, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import i18n, { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { WhenWritable } from '../../../../-components/when-writable'
import { formatRomeMoment } from '../../../../-lib/rome-time'
import { formatScore } from '../../-lib/score'
import { rankingReportQueryOptions } from '../-query/query-options'
import { ReportCloseForm } from './-components/report-close-form'

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/graduatoria/segnalazioni/$reportId/'
)({
	// The loader reads a student's data and logs the access: only opening the
	// page runs it, never a hover on a link.
	preload: false,
	loader: async ({ context, params }) => {
		try {
			await context.queryClient.ensureQueryData({
				...rankingReportQueryOptions(
					params.residenceId,
					params.reportId
				),
				revalidateIfStale: true
			})
		} catch (error) {
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				(error.code === 'ranking_report_not_found' ||
					error.code === 'residence_not_found')
			) {
				throw notFound()
			}
			throw error
		}
	},
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<ManagerRankingReport>(
				queryClient,
				makeQueryKeys.ranking.report(
					params.residenceId ?? '',
					params.reportId ?? ''
				),
				(report) =>
					`${i18n.t(`admin-ranking-reports:type.${report.type}`)} · ${report.student.protocol}`
			)
	},
	component: ReportPage
})

/**
 * One report (FFD 2.8): what the student says and the file that backs it, the
 * candidate where it stands in the ranking in force with the way to the
 * application, and the closing: with no correction, or naming the fact that
 * corrected it.
 */
function ReportPage() {
	const { t, i18n: i18nInstance } = useTranslation()
	const { residenceId, reportId } = Route.useParams()
	const { data: report } = useSuspenseQuery(
		rankingReportQueryOptions(residenceId, reportId)
	)
	const language = i18nInstance.language
	const open = report.state === 'open'

	return (
		<main className='mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-6'>
			<header className='flex flex-col gap-2'>
				<div className='flex flex-wrap items-center gap-3'>
					<h1 className='font-semibold text-2xl'>
						{t(`admin-ranking-reports:type.${report.type}`)}
					</h1>
					<Badge variant={open ? 'info' : 'neutral'}>
						{t(`admin-ranking-reports:state.${report.state}`)}
					</Badge>
				</div>
				<p className='text-muted-foreground text-sm tabular-nums'>
					{t('admin-ranking-reports:row.sent', {
						when: formatRomeMoment(report.createdAt, language)
					})}
					{' · '}
					{t('admin-ranking-reports:card.version', {
						number: report.versionNumber
					})}
				</p>
			</header>
			<DetailSection
				icon={MessageSquare}
				title={t('admin-ranking-reports:card.text')}
			>
				{report.description ? (
					<p className='whitespace-pre-line text-sm'>
						{report.description}
					</p>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t('admin-ranking-reports:card.text_erased')}
					</p>
				)}
				{report.hasFile && (
					<ReportFile reportId={reportId} residenceId={residenceId} />
				)}
			</DetailSection>
			<DetailSection
				icon={UserRound}
				title={t('admin-ranking-reports:card.candidate')}
			>
				<p className='text-sm'>
					{[report.student.lastName, report.student.firstName]
						.filter(Boolean)
						.join(' ')}
					{' · '}
					<span className='tabular-nums'>
						{t('admin-ranking-reports:row.protocol', {
							protocol: report.student.protocol
						})}
					</span>
				</p>
				<Candidate report={report} />
				<Link
					className='text-sm underline underline-offset-4'
					params={{
						residenceId,
						submissionId: report.student.submissionId
					}}
					to='/bando/$residenceId/domande/$submissionId'
				>
					{t('admin-ranking-reports:card.open_application')}
				</Link>
			</DetailSection>
			{open ? (
				<WhenWritable>
					<ReportCloseForm
						report={report}
						residenceId={residenceId}
					/>
				</WhenWritable>
			) : (
				<Closed report={report} />
			)}
		</main>
	)
}

function Candidate({ report }: { report: ManagerRankingReport }) {
	const { t, i18n: i18nInstance } = useTranslation()
	const { candidate } = report
	if (!candidate.outcome) {
		return (
			<p className='text-muted-foreground text-sm'>
				{t('admin-ranking-reports:card.candidate_none')}
			</p>
		)
	}
	return (
		<div className='flex flex-col gap-1 text-sm'>
			{candidate.listPosition !== null && candidate.list && (
				<p>
					{t('admin-ranking-reports:card.candidate_position', {
						position: candidate.listPosition,
						list: t(`admin-ranking-reports:list.${candidate.list}`),
						score: formatScore(
							candidate.score,
							i18nInstance.language
						),
						number: candidate.versionNumber
					})}
				</p>
			)}
			<p>
				{t('admin-ranking-reports:card.candidate_outcome', {
					outcome: t(
						`admin-ranking-reports:outcome.${candidate.outcome}`
					)
				})}
			</p>
		</div>
	)
}

function Closed({ report }: { report: ManagerRankingReport }) {
	const { t, i18n: i18nInstance } = useTranslation()
	const when = formatRomeMoment(report.closedAt ?? '', i18nInstance.language)
	return (
		<DetailSection
			icon={CheckCircle2}
			title={t('admin-ranking-reports:card.answer')}
		>
			<p className='whitespace-pre-line text-sm'>{report.response}</p>
			<p className='text-muted-foreground text-xs'>
				{report.closedByName
					? t('admin-ranking-reports:card.closed_by', {
							name: report.closedByName,
							when
						})
					: t('admin-ranking-reports:card.closed_by_unknown', {
							when
						})}
			</p>
			{report.fact && (
				<p className='text-muted-foreground text-xs'>
					{t('admin-ranking-reports:card.fact', {
						fact: t(
							`admin-ranking-reports:fact_type.${report.fact.type}`
						)
					})}
				</p>
			)}
		</DetailSection>
	)
}

function ReportFile({
	residenceId,
	reportId
}: {
	residenceId: string
	reportId: string
}) {
	const { t } = useTranslation()
	const download = useMutation({
		mutationFn: async () =>
			(
				await rankingDownloadReportFile({
					body: { data: { residenceId, reportId } }
				})
			).data.url,
		onSuccess: (url) => startDownload(url, 'segnalazione'),
		onError: () => toast.error(t('admin-ranking-reports:card.file_failed'))
	})
	return (
		<div>
			<Button
				disabled={download.isPending}
				onClick={() => download.mutate()}
				type='button'
				variant='outline'
			>
				<Download />
				{t('admin-ranking-reports:card.file')}
			</Button>
		</div>
	)
}
